import { type ReactNode } from 'react';

import { type AirActionTrackingLocation, type ImportOptions } from '@air/analytics';
import {
  AtomIcon,
  BoardAddIcon,
  BoardDuplicateIcon,
  BoxLogoIcon,
  BrandfolderLogoIcon,
  CantoIcon,
  CircleMinusIcon,
  CopyPlusIcon,
  DownloadIcon,
  DropboxIcon,
  Edit2Icon,
  FileInputIcon,
  FilePenLineIcon,
  FileStackIcon,
  FinderIcon,
  FolderUpIcon,
  FormIcon,
  FrameIoIcon,
  GoogleDriveIcon,
  ImagesIcon,
  InboxIcon,
  InfoIcon,
  LightbulbIcon,
  LinkIcon,
  LogOutIcon,
  Maximize2Icon,
  MegaphoneIcon,
  MergeIcon,
  MessagesSquareIcon,
  MinusIcon,
  MonitorStopIcon,
  MousePointerClickIcon,
  MoveBoardIcon,
  PhoneIcon,
  PlusIcon,
  SettingsIcon,
  SharePointIcon,
  SlackIcon,
  SquarePlayIcon,
  SquarePlusIcon,
  SquareSplitHorizontalIcon,
  StarFilledIcon,
  StarIcon,
  TagsIcon,
  Trash2Icon,
  TvMinimalIcon,
  UploadIcon,
} from '@air/design-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

import { PromptInCanvasIcon } from '~/constants/PromptInCanvasIcon';
import { CUSTOM_FIELDS_CONTEXT_MENU_EDIT } from '~/constants/testIDs';
import { type IconType } from '~/types/icons';

export type GetItemMenuOptionsCallback<T> = (params: { item: T }) => GetItemMenuOptions;

export type GetItemMenuOptions = (params: { trackLocation: AirActionTrackingLocation }) => DropdownMenuOption[];

export type { ImportOptions };

export type MenuOptionsName =
  | ImportOptions
  | 'account-settings'
  | 'add-to-context'
  | 'add-sub-board'
  | 'add-to'
  | 'add-to-board'
  | 'add-to-canvas'
  | 'add-to-share-link'
  | 'bulk-edit-custom-fields'
  | 'chat-with-support'
  | 'compare'
  | 'connect-to-slack'
  | 'connect-to-slack-upgrade'
  | 'connected-to-slack'
  | 'content-collection'
  | 'convert-to-gif-in-canvas'
  | 'copy-to-air'
  | 'create-custom-field'
  | 'create-new-board'
  | 'create-new-canvas'
  | 'download-adobe-extension'
  | 'download-air-flow'
  | 'edit-all-fields-with-tasks'
  | 'edit-custom-fields'
  | 'edit-in-canvas'
  | 'edit-tags'
  | 'export-as'
  | 'delete'
  | 'delete-version'
  | 'delete-tag'
  | 'download'
  | 'download-proxy'
  | 'duplicate'
  | 'duplicate-asset'
  | 'duplicate-canvas'
  | 'duplicate-file'
  | 'edit'
  | 'edit-inline'
  | 'favorite'
  | 'guests-can-comment'
  | 'guests-can-edit'
  | 'guests-can-view'
  | 'guests-remove-self'
  | 'guests-remove'
  | 'help-center'
  | 'import'
  | 'leave-feedback'
  | 'new-at-air'
  | 'log-out'
  | 'meet-with-sales'
  | 'merge'
  | 'move'
  | 'notifications-settings'
  | 'open-asset'
  | 'open-in-canvas'
  | 'prompt-in-canvas'
  | 'remove-from-board'
  | 'remove-from-share-link-collection'
  | 'remove-from-version-stack'
  | 'rename'
  | 'rename-files-in-bulk'
  | 'rename-inline'
  | 'rename-tag'
  | 'save-to-air'
  | 'set-as'
  | 'set-board-cover'
  | 'set-board-thumbnail'
  | 'share'
  | 'smart-resize-in-canvas'
  | 'unfavorite'
  | 'upload'
  | 'upload-files'
  | 'upload-folders'
  | 'view-all-tagged'
  | 'white-delete'
  | 'dark-delete'
  | 'open-on-desktop';

export type MenuOptionValue = {
  id: string;
  Icon?: IconType;
  iconColor?: string;
  label: ReactNode;
  labelColor?: string;
  description?: string;
  'data-testid'?: string;
};

/**
 * Source of truth for all actions available in a menu, across Clip, Board, Login, etc in any combination.
 */
export const MenuOptions: Record<MenuOptionsName, MenuOptionValue> = {
  'account-settings': {
    id: 'account-settings',
    Icon: SettingsIcon,
    label: 'Account Settings',
  },
  'edit-tags': {
    id: 'edit-tags',
    Icon: TagsIcon,
    label: 'Edit tags',
  },
  'add-to-context': {
    id: 'add-to-context',
    Icon: AtomIcon,
    label: 'Add to context',
  },
  'add-to': {
    id: 'add-to',
    Icon: SquarePlusIcon,
    label: 'Add to',
  },
  'add-to-board': {
    id: 'add-to-board',
    Icon: BoardAddIcon,
    label: 'Add to board',
  },
  'add-to-canvas': {
    id: 'add-to-canvas',
    Icon: MousePointerClickIcon,
    label: 'Add to Canvas\u2026',
  },
  'edit-in-canvas': {
    id: 'edit-in-canvas',
    Icon: MousePointerClickIcon,
    label: 'Edit in Canvas',
  },
  'add-sub-board': {
    id: 'add-sub-board',
    Icon: BoardAddIcon,
    label: 'Add Sub-board',
  },
  'add-to-share-link': {
    id: 'add-to-share-link',
    Icon: LinkIcon,
    label: 'Add to showcase',
  },
  'bulk-edit-custom-fields': {
    id: 'bulk-edit-custom-field',
    Icon: FormIcon,
    label: 'Bulk edit custom field',
  },
  'edit-all-fields-with-tasks': {
    id: 'edit-all-fields-with-tasks',
    Icon: FormIcon,
    label: 'Edit all fields with Tasks',
  },
  'chat-with-support': {
    id: 'chat-with-support',
    Icon: MessagesSquareIcon,
    label: 'Chat with support',
  },
  compare: {
    id: 'compare',
    Icon: SquareSplitHorizontalIcon,
    label: 'Compare',
  },
  'connect-to-slack': {
    id: 'connect-to-slack',
    Icon: SlackIcon,
    label: 'Connect to Slack',
  },
  'connect-to-slack-upgrade': {
    id: 'connect-to-slack-upgrade',
    Icon: SlackIcon,
    label: 'Connect to Slack',
  },
  'connected-to-slack': {
    id: 'connected-to-slack',
    Icon: SlackIcon,
    label: 'Connected to Slack',
  },
  'download-adobe-extension': {
    id: 'download-air-for-adobe-premiere-pro',
    Icon: SquarePlayIcon,
    label: 'Download Air for Premiere Pro',
  },
  'download-air-flow': {
    id: 'download-air-flow',
    Icon: MonitorStopIcon,
    label: 'Download Air Flow',
  },
  'edit-custom-fields': {
    id: 'edit-custom-fields',
    'data-testid': CUSTOM_FIELDS_CONTEXT_MENU_EDIT,
    Icon: FormIcon,
    label: 'Edit custom fields',
  },
  'create-custom-field': { id: 'create-custom-field', label: '+ Create new custom field' },
  'create-new-board': {
    id: 'create-new-board',
    Icon: BoardAddIcon,
    label: 'Create new board',
  },
  'create-new-canvas': {
    id: 'create-new-canvas',
    Icon: PlusIcon,
    label: 'Create new Canvas',
  },
  delete: {
    id: 'delete',
    Icon: Trash2Icon,
    iconColor: 'flamingo500',
    label: 'Delete from workspace',
    labelColor: 'flamingo800',
  },
  'dark-delete': {
    id: 'dark-delete',
    Icon: Trash2Icon,
    iconColor: 'flamingo300',
    label: 'Delete from workspace',
    labelColor: 'flamingo300',
  },
  'white-delete': {
    id: 'white-delete',
    Icon: Trash2Icon,
    label: 'Delete from workspace',
    'data-testid': 'white-delete',
  },
  'delete-version': {
    id: 'delete-version',
    label: 'Delete version',
    labelColor: 'flamingo800',
  },
  'delete-tag': {
    id: 'delete-tag',
    label: 'Delete tag',
    labelColor: 'flamingo800',
    'data-testid': 'delete-tag',
  },
  download: {
    id: 'download',
    Icon: DownloadIcon,
    label: 'Download',
  },
  'download-proxy': {
    id: 'download-proxy',
    label: 'Download proxy',
    Icon: DownloadIcon,
  },
  duplicate: {
    id: 'duplicate',
    Icon: BoardDuplicateIcon,
    label: 'Duplicate board',
  },
  'duplicate-asset': {
    id: 'duplicate-asset',
    Icon: CopyPlusIcon,
    label: 'Duplicate asset',
  },
  'duplicate-canvas': {
    id: 'duplicate-canvas',
    Icon: CopyPlusIcon,
    label: 'Duplicate Canvas',
  },
  'duplicate-file': {
    id: 'duplicate-file',
    Icon: CopyPlusIcon,
    label: 'Duplicate file',
  },
  edit: {
    id: 'edit',
    Icon: Edit2Icon,
    label: 'Edit details',
  },
  'edit-inline': {
    id: 'edit-inline',
    Icon: Edit2Icon,
    label: 'Rename',
  },
  'export-as': {
    id: 'export-as',
    Icon: DownloadIcon,
    label: 'Export as',
  },
  favorite: {
    id: 'favorite',
    Icon: StarIcon,
    label: 'Favorite',
  },
  'help-center': {
    id: 'help-center',
    Icon: InfoIcon,
    label: 'Help center',
  },
  import: {
    id: 'import',
    Icon: UploadIcon,
    label: 'Import from...',
  },
  'import-from-drive': {
    id: 'import-from-drive',
    Icon: GoogleDriveIcon,
    label: 'Google Drive',
  },
  'import-from-dropbox': {
    id: 'import-from-dropbox',
    Icon: DropboxIcon,
    iconColor: '#0061FF',
    label: 'Dropbox',
  },
  'import-from-box': {
    id: 'import-from-box',
    Icon: BoxLogoIcon,
    iconColor: '#0061D5',
    label: 'Box',
  },
  'import-from-brandfolder': {
    id: 'import-from-brandfolder',
    Icon: BrandfolderLogoIcon,
    label: 'Brandfolder',
  },
  'import-from-canto': {
    id: 'import-from-canto',
    Icon: CantoIcon,
    label: 'Canto',
  },
  'import-from-sharePoint': {
    id: 'import-from-sharePoint',
    Icon: SharePointIcon,
    iconColor: '#0061D5',
    label: 'SharePoint',
  },
  'import-from-frameio': {
    id: 'import-from-frameio',
    Icon: FrameIoIcon,
    iconColor: '#5B53FF',
    label: 'Frame.io',
  },
  'log-out': {
    id: 'log-out',
    Icon: LogOutIcon,
    label: 'Log out',
  },
  'guests-can-comment': {
    id: 'guests-can-comment',
    label: 'Can Comment',
    description: 'Can view and comment, but cannot add, edit, or delete any content',
  },
  'guests-can-edit': {
    id: 'guests-can-edit',
    label: 'Can Edit',
    description: 'Can edit all content on this board, but cannot delete the board itself',
  },
  'guests-can-view': {
    id: 'guests-can-view',
    label: 'Can View',
    description: 'Can view all content, but cannot view or contribute to discussions',
  },
  'guests-remove-self': {
    id: 'guests-remove-self',
    label: 'Leave this Board',
  },
  'guests-remove': {
    id: 'guests-remove',
    label: 'Remove from board',
  },
  'leave-feedback': {
    id: 'leave-feedback',
    Icon: LightbulbIcon,
    label: 'Leave feedback',
  },
  'new-at-air': {
    id: 'new-at-air',
    Icon: MegaphoneIcon,
    label: 'New at Air',
  },
  'meet-with-sales': {
    id: 'meet-with-sales',
    Icon: PhoneIcon,
    label: 'Meet with sales',
  },
  move: {
    id: 'move',
    Icon: MoveBoardIcon,
    label: 'Move to…',
  },
  'notifications-settings': {
    id: 'notifications-settings',
    label: 'Notifications settings',
  },
  'open-asset': {
    id: 'open-asset',
    Icon: Maximize2Icon,
    label: 'Open',
  },
  'open-in-canvas': {
    id: 'open-in-canvas',
    Icon: MousePointerClickIcon,
    label: 'More edits in Canvas',
  },
  'prompt-in-canvas': {
    id: 'prompt-in-canvas',
    Icon: PromptInCanvasIcon,
    label: 'Prompt',
  },
  'remove-from-board': {
    id: 'remove-from-board',
    Icon: MinusIcon,
    label: 'Remove from this board',
  },
  'remove-from-share-link-collection': {
    id: 'remove-from-share-link-collection',
    Icon: CircleMinusIcon,
    label: 'Remove from showcase',
  },
  'remove-from-version-stack': {
    id: 'remove-from-version-stack',
    label: 'Remove from version stack',
  },
  rename: {
    id: 'rename',
    Icon: Edit2Icon,
    label: 'Rename',
  },
  'rename-files-in-bulk': {
    id: 'rename-files-in-bulk',
    Icon: FilePenLineIcon,
    label: 'Rename files in bulk',
  },
  'rename-inline': {
    id: 'rename-inline',
    Icon: Edit2Icon,
    label: 'Rename',
  },
  'rename-tag': {
    id: 'rename-tag',
    label: 'Rename',
  },
  'content-collection': {
    id: 'content-collection',
    Icon: InboxIcon,
    label: 'Collect content',
  },
  'convert-to-gif-in-canvas': {
    id: 'convert-to-gif-in-canvas',
    Icon: FileInputIcon,
    label: 'Convert to gif',
  },
  'smart-resize-in-canvas': {
    id: 'smart-resize-in-canvas',
    Icon: FileStackIcon,
    label: 'Smart resize',
  },
  'save-to-air': {
    id: 'save-to-air',
    Icon: MoveBoardIcon,
    label: 'Save to Air workspace',
  },
  'set-as': {
    id: 'set-as',
    Icon: ImagesIcon,
    label: 'Set as',
  },
  'set-board-cover': {
    id: 'set-board-cover',
    Icon: TvMinimalIcon,
    label: 'Set as board cover',
    'data-testid': 'set-board-cover',
  },
  'set-board-thumbnail': {
    id: 'set-board-thumbnail',
    Icon: ImagesIcon,
    label: 'Set as board thumbnail',
    'data-testid': 'set-board-thumbnail',
  },
  share: {
    id: 'share',
    Icon: LinkIcon,
    label: 'Share a link',
  },
  upload: {
    id: 'upload',
    Icon: UploadIcon,
    label: 'Upload',
  },
  unfavorite: {
    id: 'unfavorite',
    Icon: StarFilledIcon,
    label: 'Unfavorite',
  },
  'copy-to-air': {
    id: 'copy-to-air',
    Icon: MoveBoardIcon,
    label: 'Copy to Air workspace',
  },
  'upload-files': {
    id: 'upload-files',
    Icon: UploadIcon,
    label: 'File Upload',
  },
  'upload-folders': {
    id: 'upload-folders',
    Icon: FolderUpIcon,
    label: 'Folder Upload',
  },
  'view-all-tagged': {
    id: 'view-all-tagged',
    label: 'View all tagged',
  },
  'open-on-desktop': {
    id: 'open-on-desktop',
    label: 'Open in Finder',
    Icon: FinderIcon,
  },
  merge: { id: 'merge', Icon: MergeIcon, label: 'Merge', 'data-testid': 'merge' },
};

const isMenuOptionsName = (key: string): key is MenuOptionsName => Object.hasOwn(MenuOptions, key);

export const getMenuOption = (key: string | undefined): MenuOptionValue | null => {
  if (!key || !isMenuOptionsName(key)) {
    return null;
  }

  return MenuOptions[key];
};
